import { useState, useMemo, useEffect } from 'react';
import type { MockNCR, MockInspection, MockInspectionResult } from '@/mocks/qms';
import { qcService } from '@/services/qcService';
import { getAllOperators } from '@/api/operators.api';
import { useToast } from '@/contexts/ToastContext';
import { useWarehouseStore } from '@/stores/warehouseStore';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

interface Props {
  prefillInspection: MockInspection | null;
  onClose: () => void;
  onCreated: (ncr: MockNCR) => void;
}

interface OperatorState {
  id: string;
  name: string;
  employee_code: string;
  skill: string;
}

export default function RaiseNCRSlideOver({ prefillInspection, onClose, onCreated }: Props) {
  const toast = useToast();
  const selectedWarehouseId = useWarehouseStore((state) => state.selectedWarehouseId);
  const [inspectionId, setInspectionId] = useState(prefillInspection?.id || '');
  const [severity, setSeverity] = useState<MockNCR['severity']>('MAJOR');
  const [defectDescription, setDefectDescription] = useState('');
  const [defectQty, setDefectQty] = useState(1);
  const [dispositionAction, setDispositionAction] = useState<MockNCR['dispositionAction']>('PENDING');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [failedParamsSelected, setFailedParamsSelected] = useState<string[]>([]);

  const [inspections, setInspections] = useState<MockInspection[]>([]);
  const [inspectionResults, setInspectionResults] = useState<MockInspectionResult[]>([]);
  const [operators, setOperators] = useState<OperatorState[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    async function loadData() {
      try {
        setLoading(true);
        const [inspData, opRes] = await Promise.all([
          qcService.getInspections({ warehouseId: selectedWarehouseId || undefined }),
          getAllOperators(),
        ]);
        if (!active) return;

        setInspections(inspData || []);
        if (opRes.success && opRes.data) {
          setOperators(opRes.data);
        }
      } catch (err) {
        console.error('Failed to load master data in RaiseNCRSlideOver:', err);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadData();
    return () => {
      active = false;
    };
  }, [selectedWarehouseId]);

  const failedInspections = useMemo(() => {
    const filtered = inspections.filter((i) => i.status === 'FAILED');
    return filtered.length > 0 ? filtered : inspections;
  }, [inspections]);

  const selectedInspection = useMemo(() => {
    return inspections.find((i) => i.id === inspectionId) || null;
  }, [inspectionId, inspections]);

  useEffect(() => {
    if (!inspectionId) {
      setInspectionResults([]);
      return;
    }
    let active = true;
    async function fetchResults() {
      try {
        const details = await qcService.getInspectionById(inspectionId);
        if (active && details && details.results) {
          setInspectionResults(details.results);
        }
      } catch (err) {
        console.error('Error fetching inspection results for NCR:', err);
      }
    }
    fetchResults();
    return () => {
      active = false;
    };
  }, [inspectionId]);

  const failedParams = useMemo(() => {
    if (!selectedInspection) return [];
    return inspectionResults.filter(
      (r) => r.status === 'FAIL' || r.status === 'FAILED'
    );
  }, [selectedInspection, inspectionResults]);

  const inspectionOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select an inspection --' },
    ...failedInspections.map((i) => ({
      value: i.id,
      label: `${i.inspectionNumber} — ${i.itemName}`,
      sublabel: i.status
    }))
  ], [failedInspections]);

  const dispositionOptions: SearchableOption[] = useMemo(() => [
    { value: 'PENDING', label: 'Select disposition...' },
    { value: 'REJECT', label: 'Reject' },
    { value: 'REWORK', label: 'Rework' },
    { value: 'USE_AS_IS', label: 'Use As Is' },
    { value: 'RETURN_TO_SUPPLIER', label: 'Return to Supplier' }
  ], []);

  const assignedToOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: 'Unassigned' },
    ...operators.map((o) => ({
      value: o.id,
      label: o.name,
      sublabel: o.skill ? o.skill.replace(/_/g, ' ') : undefined
    }))
  ], [operators]);

  const toggleParam = (name: string) => {
    setFailedParamsSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const save = async () => {
    if (!selectedInspection) {
      toast.error('Please select an inspection');
      return;
    }
    if (!defectDescription.trim()) {
      toast.error('Defect description is required');
      return;
    }
    if (!dispositionAction || dispositionAction === 'PENDING') {
      toast.error('Please select a disposition action');
      return;
    }
    if (!dueDate) {
      toast.error('Due date is required');
      return;
    }

    const assignedOp = operators.find((o) => o.id === assignedTo);

    const ncrPayload: Partial<MockNCR> = {
      inspectionId: selectedInspection.id,
      inspectionNumber: selectedInspection.inspectionNumber,
      type: selectedInspection.type,
      severity,
      status: 'OPEN',
      itemId: selectedInspection.itemId,
      itemName: selectedInspection.itemName,
      variantName: selectedInspection.variantName,
      sourceNumber: selectedInspection.sourceNumber,
      failedParameters: failedParamsSelected.length > 0 ? failedParamsSelected : failedParams.map((p) => p.parameterName),
      defectDescription: defectDescription.trim(),
      defectQty,
      unit: selectedInspection.unit,
      dispositionAction,
      assignedTo: assignedTo || null,
      assignedToName: assignedOp?.name || null,
      dueDate: dueDate || null,
      createdBy: 'Admin User',
    };

    try {
      const createdNCR = await qcService.createNCR(ncrPayload);
      onCreated(createdNCR);
      toast.success(`NCR ${createdNCR.ncrNumber || 'raised'} created`);
      onClose();
    } catch (err) {
      console.error('Error creating NCR:', err);
      toast.error('Failed to create NCR');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Raise NCR</h2>
            <p className="text-sm text-gray-500">Create a new non-conformance report</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
            <i className="ri-close-line text-gray-500 text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Inspection */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Linked Inspection <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={inspectionId}
              onChange={(val) => setInspectionId(val)}
              options={inspectionOptions}
              placeholder="-- Select an inspection --"
              searchPlaceholder="Search inspection..."
            />
            {loading && <p className="text-xs text-gray-400 mt-1">Loading inspections...</p>}
          </div>

          {selectedInspection && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Item</span>
                <span className="font-medium text-gray-900">{selectedInspection.itemName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Source</span>
                <span className="text-gray-700">{selectedInspection.sourceNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Type</span>
                <span className="text-gray-700">{selectedInspection.type?.replace(/_/g, ' ')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Failed Qty</span>
                <span className="font-medium text-red-600">{selectedInspection.failedQty || selectedInspection.sampleQty} {selectedInspection.unit}</span>
              </div>
            </div>
          )}

          {/* Severity */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Severity <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {(['MINOR', 'MAJOR', 'CRITICAL'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSeverity(s)}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border whitespace-nowrap cursor-pointer ${
                    severity === s
                      ? s === 'MINOR'
                        ? 'border-sky-500 bg-sky-50 text-sky-700'
                        : s === 'MAJOR'
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Failed Parameters */}
          {failedParams.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Failed Parameters</label>
              <div className="space-y-2">
                {failedParams.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={failedParamsSelected.includes(p.parameterName)}
                      onChange={() => toggleParam(p.parameterName)}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-sm text-gray-700">{p.parameterName}</span>
                    <span className="text-xs text-gray-400">({p.observedValue})</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Defect Description */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Defect Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={defectDescription}
              onChange={(e) => setDefectDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              placeholder="Describe the defect in detail..."
            />
          </div>

          {/* Defect Qty */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Defect Qty</label>
            <input
              type="number"
              min={1}
              value={defectQty}
              onChange={(e) => setDefectQty(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          {/* Disposition */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Disposition Action <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={dispositionAction}
              onChange={(val) => setDispositionAction(val as MockNCR['dispositionAction'])}
              options={dispositionOptions}
              placeholder="Select disposition..."
            />
          </div>

          {/* Assigned To */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Assigned To</label>
            <SearchableSelect
              value={assignedTo}
              onChange={(val) => setAssignedTo(val)}
              options={assignedToOptions}
              placeholder="Unassigned"
              searchPlaceholder="Search operator..."
            />
          </div>

          {/* Due Date */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Due Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={save}
            className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-1" />
            Raise NCR
          </button>
        </div>
      </div>
    </div>
  );
}